import * as React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box, SelectOption } from '@factorialco/deprecated-design-system'
import {
  AdditionalCompensationFormFieldsFragment,
  ContractsValueObjectsAmountStrategyTypeEnum,
  PayrollConceptSelectFieldFragment,
} from 'generated/resources/graphql'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import MonthPicker from 'modules/core/components/MonthPicker'
import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { PayrollConceptSelectField } from 'modules/compensations/components/PayrollConceptSelectField'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import { createSelectOptions } from 'modules/contracts/lib/utils'

import { AmountStrategyInputsFields } from './amountStrategyInputs/amountStrategyInputs'

type AdditionalCompensationFormFieldsProps = {
  additionalCompensation?: AdditionalCompensationFormFieldsFragment
  isEditable?: boolean
  payrollConcepts: PayrollConceptSelectFieldFragment[]
}

function AdditionalCompensationFormFields({
  additionalCompensation,
  isEditable = true,
  payrollConcepts,
}: AdditionalCompensationFormFieldsProps) {
  const additionalCompensationForm = useFormContext<AdditionalCompensationFormFieldsFragment>()
  const { control, getValues, setValue, trigger, watch } = additionalCompensationForm

  const payrollConceptId = watch('payrollConceptId')

  const amountStrategyTypeWatch = watch('amountStrategyType')
  const payrollConceptsOptions = useCategorizedPayrollConcepts(payrollConcepts)

  React.useEffect(() => {
    const selectedPayrollConcept = payrollConceptsOptions.find(
      (pco) => pco.value === payrollConceptId
    )
    const selectedPayrollConceptName = selectedPayrollConcept?.label ?? ''
    setValue('payrollConcept.label', selectedPayrollConceptName)
    const selectedPayrollConceptUnitType =
      payrollConcepts !== undefined &&
      payrollConcepts?.find((pc) => pc.id === payrollConceptId)?.unitType
    if (selectedPayrollConceptUnitType) {
      setValue('payrollConcept.unitType', selectedPayrollConceptUnitType)
    }
  }, [payrollConceptId, payrollConceptsOptions, setValue, payrollConcepts])

  return (
    <Box gap="s16">
      <PayrollConceptSelectField<AdditionalCompensationFormFieldsFragment>
        name="payrollConceptId"
        payrollConcepts={payrollConceptsOptions}
        label={'Payroll concept'}
        placeholder={'Select'}
        blankLabel={'empty'}
        isDisabled={!isEditable}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextArea
            {...field}
            label={'Description'}
            readonly={!isEditable}
            value={getValues('description') ?? ''}
            onChange={(value) => {
              setValue('description', value, { shouldDirty: true })
              trigger('description')
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="amountStrategyType"
        rules={{ required: true }}
        render={({ field, formState: { errors } }) => {
          const amountStrategyTypeOptions =
            createSelectOptions<ContractsValueObjectsAmountStrategyTypeEnum>([
              'contracts_fixed_amount_strategy',
              'contracts_variable_amount_strategy',
            ] as const)
          return (
            <SelectField
              {...field}
              label={'Type'}
              none={'Select'}
              options={amountStrategyTypeOptions}
              value={getValues('amountStrategyType')}
              readonly={!isEditable}
              onChange={(value) => {
                const isFixed = (value: string): value is 'contracts_fixed_amount_strategy' =>
                  value === 'contracts_fixed_amount_strategy'
                setValue(
                  'amountStrategyType',
                  isFixed(value)
                    ? 'contracts_fixed_amount_strategy'
                    : 'contracts_variable_amount_strategy',
                  {
                    shouldDirty: true,
                  }
                )
                trigger('amountStrategyType')
              }}
              onBlur={() => trigger('amountStrategyType')}
              onFocus={() => trigger('amountStrategyType')}
              error={
                errors?.amountStrategyType?.message
                  ? String(errors.amountStrategyType.message)
                  : undefined
              }
              mandatory
              secondaryLabel={
                <InformationTooltip
                  information={
                    `${i18n.t(
                      'contract_versions.additional_compensation.additional_compensation_form_fields.amounts_strategy_type_information_tooltip_1'
                    )}` +
                    `${i18n.t(
                      'contract_versions.additional_compensation.additional_compensation_form_fields.amounts_strategy_type_information_tooltip_2'
                    )}`
                  }
                  icon="info"
                />
              }
            />
          )
        }}
      />
      <AmountStrategyInputsFields
        amountStrategyType={amountStrategyTypeWatch ?? additionalCompensation?.amountStrategyType}
        readonly={!isEditable}
        missingLabel={i18n.t('common.missing_field')}
        selectValue={i18n.t('common.select')}
      />
      <Controller
        control={control}
        name="recurrence"
        defaultValue={'monthly'}
        render={({ field, formState: { errors } }) => {
          const recurrenceOptions: SelectOption[] = [
            {
              label: 'Monthly',
              value: 'monthly',
            },
            {
              label: 'Every 2 Months',
              value: 'every_2_months',
            },
            {
              label: 'Every 3 Months',
              value: 'every_3_months',
            },
            {
              label: 'Every 4 Months',
              value: 'every_4_months',
            },
            {
              label: 'Every 5 Months',
              value: 'every_5_months',
            },
            {
              label: 'Every 6 Months',
              value: 'every_6_months',
            },
            {
              label: 'Every 7 Months',
              value: 'every_7_months',
            },
            {
              label: 'Every 8 Months',
              value: 'every_8_months',
            },
            {
              label: 'Every 9 Months',
              value: 'every_9_months',
            },
            {
              label: 'Every 10 Months',
              value: 'every_10_months',
            },
            {
              label: 'Every 11 Months',
              value: 'every_11_months',
            },
            {
              label: 'Every 12 Months',
              value: 'every_12_months',
            },
          ]
          return (
            <SelectField
              {...field}
              label={'Recurrence'}
              readonly={!isEditable}
              options={recurrenceOptions}
              value={getValues('recurrence') ?? 'monthly'}
              onChange={(value) => {
                setValue('recurrence', value)
              }}
              onBlur={() => trigger('recurrence')}
              onFocus={() => trigger('recurrence')}
              error={errors?.recurrence?.message ? String(errors.recurrence.message) : undefined}
            />
          )
        }}
      />
      <Controller
        control={control}
        name="firstPaymentOn"
        render={({ field, formState: { errors } }) => {
          const value = getValues('firstPaymentOn')
          const formattedDate = value && dateBackendToUI(value)
          return (
            <MonthPicker
              {...field}
              label={'First payment'}
              readonly={!isEditable}
              value={formattedDate ?? undefined}
              onChange={(value) => {
                setValue('firstPaymentOn', dateUIToBackend(value))
              }}
              error={
                errors?.firstPaymentOn?.message ? String(errors.firstPaymentOn.message) : undefined
              }
            />
          )
        }}
      />
    </Box>
  )
}

export { AdditionalCompensationFormFields }
